import { useQuery } from '@tanstack/react-query';
import { api } from '@xtnd/web/shared/api';
import type { CustomFieldFilterConfig } from '../types';

interface CustomFieldDefinition {
  id: number;
  name: string;
  field_type: string;
  parent_type: string;
  picklist_options?: Array<{ id: number; option: string }>;
}

interface EnabledFilterWithOptions extends CustomFieldFilterConfig {
  options: string[]; // Sorted alphabetically
}

/**
 * Fetches the tenant's enabled custom field filter configs from juniper config
 */
export function useEnabledCustomFieldFilters() {
  return useQuery<CustomFieldFilterConfig[]>({
    queryKey: ['juniper-config-custom-field-filters'],
    queryFn: async () => {
      const response = await api.get<any>('/juniper/config');
      const filters: CustomFieldFilterConfig[] = response?.customFieldFilters || [];
      return filters.filter((f) => f.enabled);
    },
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

/**
 * Fetches clio custom field definitions to get picklist options for enabled filters
 */
export function useCustomFieldFilterOptions(enabledFilters: CustomFieldFilterConfig[]) {
  return useQuery<EnabledFilterWithOptions[]>({
    queryKey: ['custom-field-filter-options', enabledFilters.map((f) => f.fieldId)],
    queryFn: async () => {
      if (enabledFilters.length === 0) return [];

      const response = await api.get<any>('/clio/custom-fields');
      const allFields: CustomFieldDefinition[] = response?.data || [];

      return enabledFilters
        .map((filter) => {
          const fieldDef = allFields.find((f) => f.id === filter.fieldId);
          let options: string[] = [];

          if (filter.fieldType === 'checkbox') {
            options = ['true', 'false'];
          } else if (fieldDef?.picklist_options) {
            options = fieldDef.picklist_options
              .map((o) => o.option)
              .sort((a, b) => a.localeCompare(b));
          }

          return { ...filter, options };
        })
        .sort((a, b) => a.fieldName.localeCompare(b.fieldName));
    },
    enabled: enabledFilters.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}
